import { Product } from '@gozoki/api-types';
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from '@mui/material';
import { useState } from 'react';

interface TheftChargeModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (amountInCents: number) => Promise<void>;
    products: Product[];
    loading?: boolean;
}

const TheftChargeModal = ({
    open,
    onClose,
    onConfirm,
    products,
    loading,
}: TheftChargeModalProps) => {
    const [selections, setSelections] = useState<Map<number, number>>(new Map());

    const toggleProduct = (productId: number) => {
        setSelections((prev) => {
            const next = new Map(prev);
            if (next.has(productId)) {
                next.delete(productId);
            } else {
                next.set(productId, 1);
            }
            return next;
        });
    };

    const setQuantity = (productId: number, quantity: number) => {
        if (quantity < 1) return;
        setSelections((prev) => {
            const next = new Map(prev);
            next.set(productId, quantity);
            return next;
        });
    };

    const totalEuros = products.reduce((sum, p) => {
        const qty = selections.get(p.id) ?? 0;
        return sum + p.sellPrice * qty;
    }, 0);

    const totalCents = Math.round(totalEuros * 100);

    const handleConfirm = async () => {
        await onConfirm(totalCents);
        setSelections(new Map());
    };

    const handleClose = () => {
        setSelections(new Map());
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Débiter pour vol</DialogTitle>
            <DialogContent>
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {products.map((product) => {
                        const isSelected = selections.has(product.id);
                        const quantity = selections.get(product.id) ?? 0;
                        return (
                            <div
                                key={product.id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '4px 0',
                                    fontFamily: 'Avenir Next',
                                }}
                            >
                                <Checkbox
                                    checked={isSelected}
                                    onChange={() => toggleProduct(product.id)}
                                />
                                <span style={{ flex: 1 }}>{product.label}</span>
                                <span style={{ width: '60px', textAlign: 'right' }}>
                                    {product.sellPrice.toFixed(2)}€
                                </span>
                                {isSelected && (
                                    <TextField
                                        type="number"
                                        size="small"
                                        value={quantity}
                                        onChange={(e) =>
                                            setQuantity(
                                                product.id,
                                                parseInt(e.target.value, 10) || 1
                                            )
                                        }
                                        inputProps={{ min: 1 }}
                                        style={{ width: '70px' }}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
                <div
                    style={{
                        marginTop: '16px',
                        fontFamily: 'Avenir Next',
                        fontWeight: 600,
                        fontSize: '18px',
                        textAlign: 'right',
                    }}
                >
                    Total: {totalEuros.toFixed(2)}€
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Annuler</Button>
                <Button
                    variant="contained"
                    color="error"
                    onClick={handleConfirm}
                    disabled={totalCents === 0 || loading}
                >
                    Confirmer le débit
                    {loading && (
                        <CircularProgress size={24} color="info" style={{ marginLeft: 8 }} />
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TheftChargeModal;
